// ═══════════════════════════════════════════════════════════════════════════
//  A20 · C1 — the bot fence in front of the licence console's ANONYMOUS
//  reporter (/login and /reset-password, both outside ProtectedArea).
//
//  ── PROVENANCE ──────────────────────────────────────────────────────────────
//  PORTED COPY. Original: TPRM7 packages/ui/src/reporter-anon/TurnstileGate.tsx
//  (which in turn is apps/trust's, moved into the shared package). Identical
//  below this header EXCEPT for the import path and the removal of the
//  `'use client'` directive — this is a Vite SPA with no RSC boundary, and
//  Rollup warns on module-level directives it cannot honour. Same reduction the
//  rest of this directory's ported files carry; see ReportProvider.tsx's header.
//
//  The two repos do not share a package, so this file is duplicated rather than
//  imported. Keep it diffable with the TPRM7 original — the two halves of one
//  security control drifting apart is the failure this note exists to prevent.
//
//  ── WHY THIS EXISTS AT ALL ──────────────────────────────────────────────────
//  /login and /reset-password have no session, so they cannot use
//  POST /api/v1/reports/mine (supabaseAuthMiddleware). Their only inbound path
//  is POST /api/v1/reports/anonymous on this very server, which fails CLOSED in
//  both directions (bug-report.routes.ts:105-145):
//     · TURNSTILE_SECRET_KEY unset ⇒ 501 for every request
//     · no / invalid `turnstileToken` in the body ⇒ 401
//  There is deliberately no `if (!secret) return true` branch there — A18 found
//  exactly that shape in trust-profile-invite — so a report from the sign-in
//  screen is only ever accepted with a live token, which this component mints.
//
//  ── THE WIDGET IS MOUNTED ONLY WHILE THE DIALOG IS OPEN ─────────────────────
//  The sign-in screen is the first thing an operator sees. Loading Cloudflare's
//  challenge script on every paint would put a third-party beacon in front of
//  everyone who will never file anything, so the script + widget are pulled in
//  on the first open of the report dialog and torn down when it closes.
//
//  ── KNOWN LIMIT, STATED RATHER THAN HIDDEN ──────────────────────────────────
//  `appearance: 'interaction-only'` means the widget is invisible for the
//  common case and only paints when Cloudflare demands an interaction. When it
//  does paint it sits above the dialog with pointer-events restored, but the
//  dialog's focus trap still owns keyboard focus, so a keyboard-only user may
//  not be able to complete an interactive challenge. In that case `take()`
//  resolves null, the transport refuses to submit and says so. It never reports
//  a success that did not happen.
// ═══════════════════════════════════════════════════════════════════════════

import * as React from 'react';
import { useReporterOptional } from '../ReportProvider';

/** How long a submit will wait for a token before giving up honestly. */
const TOKEN_WAIT_MS = 20_000;

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id?: string) => void;
  remove: (id?: string) => void;
}

declare global {
  // NOTE vs the TPRM7 original: no `eslint-disable no-var` here. This repo's
  // flat config does not enable that rule and reports the directive itself as
  // an unused-disable error (`--report-unused-disable-directives`). `var` is
  // mandatory in a `declare global` block — `let`/`const` do not create the
  // globalThis property TypeScript needs for `window.turnstile`.
  var turnstile: TurnstileApi | undefined;
}

/** Module-level so repeated opens share ONE script tag / one load. */
let scriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
    const fail = () => {
      // Null the cache so a later open (or a retry once a content blocker is
      // switched off) gets a fresh attempt rather than a dead promise.
      scriptPromise = null;
      reject(new Error('Turnstile script failed to load'));
    };
    const settle = () => (window.turnstile ? resolve(window.turnstile) : fail());

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      if (window.turnstile) return resolve(window.turnstile);
      existing.addEventListener('load', settle);
      existing.addEventListener('error', fail);
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.addEventListener('load', settle);
    s.addEventListener('error', fail);
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export interface TurnstileGateHandle {
  /**
   * Hand over one SINGLE-USE token, minting a replacement behind it so a retry
   * after a failed submit is not blocked on a spent token.
   *
   * Resolves null when the fence could not run (script blocked, widget error,
   * or no token inside {@link TOKEN_WAIT_MS}). The caller MUST then refuse to
   * submit — posting without a token is a guaranteed 401 and would look to the
   * user like a server fault rather than a blocked challenge.
   */
  take: () => Promise<string | null>;
}

export const TurnstileGate = React.forwardRef<TurnstileGateHandle, { siteKey: string }>(
  function TurnstileGate({ siteKey }, ref) {
    const reporter = useReporterOptional();
    const open = reporter?.open ?? false;

    const hostRef = React.useRef<HTMLDivElement | null>(null);
    const widgetIdRef = React.useRef<string | null>(null);
    const tokenRef = React.useRef<string | null>(null);
    const waitersRef = React.useRef<Array<(token: string | null) => void>>([]);
    const failedRef = React.useRef(false);
    const [failed, setFailed] = React.useState(false);

    /**
     * Fail everyone waiting on a token. Used when the fence cannot run and on
     * teardown — never leave a submit hanging on a widget that is gone.
     */
    const failAll = React.useCallback(() => {
      waitersRef.current.splice(0).forEach((waiter) => waiter(null));
    }, []);

    /** Discard the spent token and ask the widget for a fresh one. */
    const refresh = React.useCallback(() => {
      tokenRef.current = null;
      const id = widgetIdRef.current;
      if (id && typeof window !== 'undefined' && window.turnstile) {
        try {
          window.turnstile.reset(id);
        } catch {
          /* the widget is already gone with the unmounted DOM node */
        }
      }
    }, []);

    const onToken = React.useCallback(
      (token: string) => {
        failedRef.current = false;
        // Tokens are SINGLE USE, so this one goes to exactly ONE waiter — never
        // to several, and never handed over AND cached. Any further waiter is
        // served by the replacement that refresh() mints.
        const waiter = waitersRef.current.shift();
        if (waiter) {
          waiter(token);
          refresh();
        } else {
          tokenRef.current = token;
        }
      },
      [refresh],
    );

    const take = React.useCallback((): Promise<string | null> => {
      return new Promise<string | null>((resolve) => {
        const cached = tokenRef.current;
        if (cached) {
          tokenRef.current = null;
          resolve(cached);
          refresh();
          return;
        }
        if (failedRef.current) {
          resolve(null);
          return;
        }

        let timer: ReturnType<typeof setTimeout> | undefined;
        let done = false;
        const settle = (token: string | null) => {
          if (done) return;
          done = true;
          if (timer !== undefined) clearTimeout(timer);
          resolve(token);
        };
        timer = setTimeout(() => {
          const i = waitersRef.current.indexOf(settle);
          if (i >= 0) waitersRef.current.splice(i, 1);
          settle(null);
        }, TOKEN_WAIT_MS);
        waitersRef.current.push(settle);
      });
    }, [refresh]);

    React.useImperativeHandle(ref, () => ({ take }), [take]);

    React.useEffect(() => {
      if (!siteKey || !open) return undefined;
      let cancelled = false;
      failedRef.current = false;
      setFailed(false);

      loadTurnstile()
        .then((api) => {
          if (cancelled || !hostRef.current || widgetIdRef.current) return;
          widgetIdRef.current = api.render(hostRef.current, {
            sitekey: siteKey,
            // Invisible unless Cloudflare actually demands an interaction.
            appearance: 'interaction-only',
            // Follow the app theme rather than the OS, so a visible challenge
            // matches the page it sits in.
            theme:
              document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light',
            callback: onToken,
            // A token that expired or timed out is no token at all.
            'expired-callback': () => {
              tokenRef.current = null;
            },
            'timeout-callback': () => {
              tokenRef.current = null;
            },
            'error-callback': () => {
              tokenRef.current = null;
              failedRef.current = true;
              setFailed(true);
              failAll();
            },
          });
        })
        .catch(() => {
          if (cancelled) return;
          failedRef.current = true;
          setFailed(true);
          failAll();
        });

      return () => {
        cancelled = true;
        const id = widgetIdRef.current;
        widgetIdRef.current = null;
        tokenRef.current = null;
        failAll();
        if (id && typeof window !== 'undefined' && window.turnstile) {
          try {
            window.turnstile.remove(id);
          } catch {
            /* the widget is already gone with the unmounted DOM node */
          }
        }
      };
    }, [siteKey, open, onToken, failAll]);

    if (!siteKey) return null;

    return (
      <div
        // Never recorded, and inert while the dialog is shut.
        className="ps-no-capture"
        aria-hidden={!open}
        style={{
          position: 'fixed',
          left: '50%',
          bottom: 24,
          transform: 'translateX(-50%)',
          zIndex: 500,
          display: open ? 'block' : 'none',
          pointerEvents: 'auto',
        }}
      >
        <div ref={hostRef} data-testid="anon-turnstile-host" />
        {failed && (
          <p
            role="alert"
            style={{
              margin: '6px 0 0',
              maxWidth: 320,
              fontSize: 12.5,
              lineHeight: 1.45,
              color: 'var(--text-secondary)',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              padding: '8px 10px',
            }}
          >
            The verification check couldn’t load. Disable any content blocker for this page, or
            email support@polestead.com instead.
          </p>
        )}
      </div>
    );
  },
);
